import type { OklchColor } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/types";

// Every orb is drawn in a square of this many units and scaled by the element's size, so blob sizes and blur read the same on a small row thumbnail as on a large card.
export const COVER_VIEW_BOX_SIZE = 120;

export const COVER_CENTER = COVER_VIEW_BOX_SIZE / 2;

// The cover is a rounded SQUARE, not a disc: a circle reads as a person — every avatar in the app is one — where these name a piece of work, and square art is what a track wears everywhere else. In viewBox units, so it scales with the cover: 9% of the square. 22%, the radius an app icon wears, makes a card-sized cover read as a pebble rather than as artwork.
export const COVER_CORNER_RADIUS = COVER_VIEW_BOX_SIZE * 0.09;

// Large enough that neighbouring blobs melt into one field; at half this the orb reads as separate spots.
export const COVER_BLUR_STANDARD_DEVIATION = 15;

// How far a palette's hues may drift per cover, in degrees either way. Enough that two covers on the same palette differ, small enough that the palette keeps its character.
export const COVER_HUE_DRIFT_DEGREES = 8;

// How far a blob may sit from its home position, in viewBox units either way.
export const COVER_BLOB_POSITION_JITTER = 10;

// Hand-picked rather than computed from a hue. Chroma stays moderate and the lights are creamy instead of white, which is what keeps the orbs from reading as plastic. OKLCH so blues and purples carry the same perceived brightness as the oranges.
export const COVER_PALETTES: {
  shadow: OklchColor;
  body: OklchColor;
  main: OklchColor;
  light: OklchColor;
  accent: OklchColor;
}[] = [
  // Ember
  {
    shadow: [0.26, 0.07, 25],
    body: [0.52, 0.17, 35],
    main: [0.7, 0.16, 55],
    light: [0.86, 0.1, 80],
    accent: [0.6, 0.19, 20],
  },
  // Peach
  {
    shadow: [0.4, 0.1, 20],
    body: [0.68, 0.14, 35],
    main: [0.8, 0.11, 60],
    light: [0.93, 0.05, 85],
    accent: [0.72, 0.15, 10],
  },
  // Rose
  {
    shadow: [0.28, 0.09, 0],
    body: [0.55, 0.18, 5],
    main: [0.72, 0.14, 350],
    light: [0.9, 0.05, 30],
    accent: [0.62, 0.16, 330],
  },
  // Orchid
  {
    shadow: [0.25, 0.09, 310],
    body: [0.5, 0.17, 320],
    main: [0.7, 0.14, 335],
    light: [0.9, 0.05, 20],
    accent: [0.6, 0.15, 290],
  },
  // Violet
  {
    shadow: [0.22, 0.09, 285],
    body: [0.45, 0.17, 290],
    main: [0.65, 0.15, 300],
    light: [0.86, 0.07, 330],
    accent: [0.72, 0.12, 20],
  },
  // Indigo
  {
    shadow: [0.2, 0.08, 265],
    body: [0.42, 0.16, 270],
    main: [0.62, 0.14, 260],
    light: [0.85, 0.06, 230],
    accent: [0.7, 0.12, 320],
  },
  // Ocean
  {
    shadow: [0.22, 0.06, 250],
    body: [0.45, 0.13, 245],
    main: [0.65, 0.12, 225],
    light: [0.88, 0.05, 200],
    accent: [0.75, 0.1, 180],
  },
  // Lagoon
  {
    shadow: [0.24, 0.05, 210],
    body: [0.5, 0.1, 200],
    main: [0.7, 0.1, 185],
    light: [0.9, 0.05, 170],
    accent: [0.8, 0.1, 100],
  },
  // Sage
  {
    shadow: [0.26, 0.04, 160],
    body: [0.5, 0.08, 160],
    main: [0.72, 0.08, 150],
    light: [0.92, 0.04, 110],
    accent: [0.8, 0.09, 80],
  },
  // Dusk
  {
    shadow: [0.22, 0.07, 280],
    body: [0.48, 0.14, 330],
    main: [0.7, 0.14, 30],
    light: [0.88, 0.08, 70],
    accent: [0.58, 0.16, 5],
  },
  // Aurora
  {
    shadow: [0.22, 0.06, 220],
    body: [0.5, 0.12, 190],
    main: [0.72, 0.12, 160],
    light: [0.88, 0.08, 110],
    accent: [0.6, 0.14, 300],
  },
  // Sand
  {
    shadow: [0.35, 0.05, 50],
    body: [0.62, 0.08, 60],
    main: [0.78, 0.07, 75],
    light: [0.93, 0.03, 90],
    accent: [0.7, 0.11, 35],
  },
  // Wine
  {
    shadow: [0.18, 0.07, 5],
    body: [0.38, 0.14, 10],
    main: [0.55, 0.16, 20],
    light: [0.78, 0.1, 50],
    accent: [0.45, 0.15, 340],
  },
  // Glacier
  {
    shadow: [0.3, 0.05, 250],
    body: [0.6, 0.08, 240],
    main: [0.8, 0.06, 220],
    light: [0.95, 0.02, 200],
    accent: [0.75, 0.09, 300],
  },
];

// Each blob has a part to play and a home: shadow low, main across the middle, accent to one side, light high. The whole field is then rotated per cover. Placing every blob at random is what made some orbs blotchy.
export const COVER_BLOB_ROLES: {
  role: "shadow" | "main" | "accent" | "light";
  homeX: number;
  homeY: number;
  radius: number;
  opacity: number;
}[] = [
  { role: "shadow", homeX: 34, homeY: 92, radius: 44, opacity: 0.95 },
  { role: "main", homeX: 74, homeY: 62, radius: 40, opacity: 0.95 },
  { role: "accent", homeX: 100, homeY: 96, radius: 30, opacity: 0.85 },
  { role: "light", homeX: 82, homeY: 26, radius: 30, opacity: 0.9 },
  { role: "main", homeX: 18, homeY: 34, radius: 26, opacity: 0.8 },
];
